import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL, WS_BASE_URL } from '../config';
import './Viewer.css';

function Viewer() {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [noActiveSession, setNoActiveSession] = useState(false);
  const [translations, setTranslations] = useState([]);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState('');
  const [showOriginal, setShowOriginal] = useState(false);
  const wsRef = useRef(null);
  const translationsEndRef = useRef(null);
  const originalPanelRef = useRef(null);
  const translationPanelRef = useRef(null);
  const isScrollingRef = useRef(false);

  const fetchCurrentSession = async () => {
    setNoActiveSession(false);
    try {
      const response = await axios.get(`${API_BASE_URL}/sessions/current`);
      setSession(response.data);
      setSessionId(response.data.id);
      setError('');
    } catch (err) {
      if (err.response?.status === 404) {
        setSession(null);
        setSessionId(null);
        setNoActiveSession(true);
        setError('');
      } else {
        console.error('Error fetching session:', err);
        setError('Failed to load session');
      }
    }
  };

  const connectWebSocket = (sid) => {
    if (!sid) return;
    const ws = new WebSocket(`${WS_BASE_URL}/ws/viewer/${sid}`);
    
    ws.onopen = () => {
      console.log('✅ Viewer WebSocket connected');
      setConnected(true);
      setError('');
    };
    
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log('📨 Received WebSocket message:', data);
      
      if (data.type === 'history') {
        console.log('📚 Loading history:', data.translations.length, 'items');
        // Load existing translations
        const combined = data.translations.map((trans, idx) => ({
          transcription: data.transcriptions[idx]?.text || '',
          translation: trans.text,
          timestamp: trans.timestamp,
        }));
        setTranslations(combined);
        console.log('✅ History loaded');
      } else if (data.type === 'update') {
        console.log('🆕 New translation:', data.transcription, '→', data.translation);
        // Add new translation
        setTranslations(prev => {
          const updated = [...prev, {
            transcription: data.transcription,
            translation: data.translation,
            timestamp: data.timestamp,
          }];
          console.log('📊 Total translations now:', updated.length);
          return updated;
        });
      }
    };
    
    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      setError('Connection error');
      setConnected(false);
    };
    
    ws.onclose = () => {
      console.log('WebSocket closed');
      setConnected(false);
    };
    
    wsRef.current = ws;
  };

  useEffect(() => {
    fetchCurrentSession();
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    connectWebSocket(sessionId);
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    // Auto-scroll to bottom when new translations arrive
    if (!isScrollingRef.current && showOriginal) {
      // Sync scroll both panels to bottom
      setTimeout(() => {
        if (originalPanelRef.current && translationPanelRef.current) {
          isScrollingRef.current = true;
          originalPanelRef.current.scrollTop = originalPanelRef.current.scrollHeight;
          translationPanelRef.current.scrollTop = translationPanelRef.current.scrollHeight;
          setTimeout(() => {
            isScrollingRef.current = false;
          }, 100);
        }
      }, 100);
    } else if (!isScrollingRef.current) {
      translationsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [translations, showOriginal]);

  // Synchronized scrolling between panels
  useEffect(() => {
    if (!showOriginal) return; // Only sync when side-by-side view is active

    const originalPanel = originalPanelRef.current;
    const translationPanel = translationPanelRef.current;

    if (!originalPanel || !translationPanel) return;

    const syncScroll = (sourcePanel, targetPanel) => {
      if (isScrollingRef.current) return;
      
      isScrollingRef.current = true;
      
      // Calculate scroll percentage (handle edge cases)
      const sourceScrollable = sourcePanel.scrollHeight - sourcePanel.clientHeight;
      const targetScrollable = targetPanel.scrollHeight - targetPanel.clientHeight;
      
      if (sourceScrollable <= 0 || targetScrollable <= 0) {
        isScrollingRef.current = false;
        return;
      }
      
      const scrollPercentage = sourcePanel.scrollTop / sourceScrollable;
      
      // Apply same percentage to target panel
      const targetScrollTop = scrollPercentage * targetScrollable;
      targetPanel.scrollTop = targetScrollTop;
      
      // Reset flag after a short delay
      setTimeout(() => {
        isScrollingRef.current = false;
      }, 50);
    };

    const handleOriginalScroll = () => {
      if (!isScrollingRef.current) {
        syncScroll(originalPanel, translationPanel);
      }
    };

    const handleTranslationScroll = () => {
      if (!isScrollingRef.current) {
        syncScroll(translationPanel, originalPanel);
      }
    };

    originalPanel.addEventListener('scroll', handleOriginalScroll);
    translationPanel.addEventListener('scroll', handleTranslationScroll);

    return () => {
      originalPanel.removeEventListener('scroll', handleOriginalScroll);
      translationPanel.removeEventListener('scroll', handleTranslationScroll);
    };
  }, [showOriginal, translations]);

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString();
  };

  if (noActiveSession) {
    return (
      <div className="viewer-container">
        <div className="viewer-card">
          <button className="back-btn" onClick={() => navigate('/')}>
            ← Back
          </button>
          <h1 className="title">👁 Translation Viewer</h1>
          <div className="empty-state no-session">
            <p>No active session</p>
            <p className="empty-subtext">Start capturing from the home page to see the live translation here.</p>
            <button className="btn btn-home" onClick={() => navigate('/')}>
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="viewer-container">
        <div className="viewer-card">
          <p>Loading session...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="viewer-container">
      <div className="viewer-card">
        <button className="back-btn" onClick={() => navigate('/')}>
          ← Back
        </button>

        <div className="header">
          <h1 className="title">👁 Translation Viewer</h1>
          <div className="header-controls">
            <button 
              className={`toggle-btn ${showOriginal ? 'active' : ''}`}
              onClick={() => setShowOriginal(!showOriginal)}
              title={showOriginal ? "Show translation only" : "Show original text"}
            >
              {showOriginal ? '📄 Translation Only' : '📋 Show Both'}
            </button>
            <div className={`connection-status ${connected ? 'connected' : 'disconnected'}`}>
              <div className="status-dot"></div>
              {connected ? 'Live' : 'Disconnected'}
            </div>
          </div>
        </div>
        
        <div className="session-info">
          <p><strong>Translation:</strong> {session.source_lang.toUpperCase()} → {session.target_lang.toUpperCase()}</p>
          <p><strong>Total Items:</strong> {translations.length}</p>
        </div>

        {error && <div className="error-message">{error}</div>}
        
        {connected && translations.length === 0 && (
          <div className="info-message">
            ✅ Connected and listening... Waiting for audio from capture device.
          </div>
        )}

        <div className="translations-container">
          {translations.length === 0 ? (
            <div className="empty-state">
              <p>🎧 Waiting for translations...</p>
              <p className="empty-subtext">Audio will appear here in real-time</p>
            </div>
          ) : showOriginal ? (
            <div className="continuous-view side-by-side-mode">
              <div className="text-panel original-panel" ref={originalPanelRef}>
                <div className="panel-header">
                  <span className="flag">{session.source_lang === 'en' ? '🇬🇧' : session.source_lang === 'fr' ? '🇫🇷' : session.source_lang === 'es' ? '🇪🇸' : session.source_lang === 'de' ? '🇩🇪' : session.source_lang === 'it' ? '🇮🇹' : '🌐'}</span>
                  <span className="language-label">{session.source_lang.toUpperCase()} ORIGINAL</span>
                </div>
                <div className="continuous-text">
                  {translations.map((item, index) => (
                    <span key={index} className="text-segment">
                      {item.transcription}
                      {index < translations.length - 1 ? ' ' : ''}
                    </span>
                  ))}
                  <span className="cursor-blink">▌</span>
                </div>
              </div>
              
              <div className="divider-arrow">→</div>
              
              <div className="text-panel translation-panel" ref={translationPanelRef}>
                <div className="panel-header">
                  <span className="flag">{session.target_lang === 'en' ? '🇬🇧' : session.target_lang === 'fr' ? '🇫🇷' : session.target_lang === 'es' ? '🇪🇸' : session.target_lang === 'de' ? '🇩🇪' : session.target_lang === 'it' ? '🇮🇹' : '🌐'}</span>
                  <span className="language-label">{session.target_lang.toUpperCase()} TRANSLATION</span>
                </div>
                <div className="continuous-text">
                  {translations.map((item, index) => (
                    <span key={index} className="text-segment">
                      {item.translation}
                      {index < translations.length - 1 ? ' ' : ''}
                    </span>
                  ))}
                  <span className="cursor-blink">▌</span>
                </div>
              </div>
              <div ref={translationsEndRef} />
            </div>
          ) : (
            <div className="continuous-view fullscreen-mode">
              <div className="text-panel translation-panel fullscreen-panel">
                <div className="panel-header">
                  <span className="flag">{session.target_lang === 'en' ? '🇬🇧' : session.target_lang === 'fr' ? '🇫🇷' : session.target_lang === 'es' ? '🇪🇸' : session.target_lang === 'de' ? '🇩🇪' : session.target_lang === 'it' ? '🇮🇹' : '🌐'}</span>
                  <span className="language-label">{session.target_lang.toUpperCase()} TRANSLATION</span>
                </div>
                <div className="continuous-text">
                  {translations.map((item, index) => (
                    <span key={index} className="text-segment">
                      {item.translation}
                      {index < translations.length - 1 ? ' ' : ''}
                    </span>
                  ))}
                  <span className="cursor-blink">▌</span>
                </div>
              </div>
              <div ref={translationsEndRef} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Viewer;
